<template>
  <div v-if="student">
    <v-row dense>
      <v-col cols="12" md="4">
        <v-row no-gutters dense>
          <!-- student info -->
          <v-col cols="12">
            <v-card color="blue lighten-5" tile flat>
              <v-card-title class="font-weight-bold text-h5">
                <div v-if="!isStudentEditMode && !isEducationEditMode">
                  <v-speed-dial
                    v-if="(isAdmin || isProfessor) && student"
                    class="mr-4"
                    small
                    v-model="fab"
                    direction="bottom"
                    transition="slide-y-transition"
                  >
                    <template v-slot:activator>
                      <v-btn v-model="fab" color="green darken-2" dark fab>
                        <v-icon v-if="fab">mdi-close</v-icon>
                        <v-icon v-else>mdi-card-account-details</v-icon>
                      </v-btn>
                    </template>
                    <v-btn fab dark small color="green" @click.native="editStudent()">
                      <v-icon>mdi-pencil</v-icon>
                    </v-btn>
                    <v-btn fab dark small color="red" @click.native="deleteStudent(student)">
                      <v-icon>mdi-delete</v-icon>
                    </v-btn>
                  </v-speed-dial>
                </div>
                <div>{{ student.displayName }}</div>
                <v-btn v-if="isStudentEditMode" color="orange" dark class="ma-2" @click="closeEditionMode()">
                  Fermer le mode édition
                  <v-icon right dark>mdi-close</v-icon>
                </v-btn>
              </v-card-title>
              <v-card-text>
                <table style="width: 100%" class="table_details_basic">
                  <tbody>
                    <tr>
                      <td>{{ descriptions.gender }}:</td>
                      <td>{{ student.gender }}</td>
                    </tr>
                    <tr>
                      <td>{{ descriptions.date_of_birth }}:</td>
                      <td>{{ student.date_of_birth | dateToHuman }}</td>
                    </tr>
                    <tr>
                      <td>{{ descriptions.marital_status }}:</td>
                      <td>{{ student.marital_status }}</td>
                    </tr>
                    <tr>
                      <td>{{ descriptions.langs }}:</td>
                      <td>{{ spokenLanguages }}</td>
                    </tr>
                    <tr>
                      <td>{{ descriptions.email }}:</td>
                      <td>{{ student.email }}</td>
                    </tr>
                    <tr>
                      <td>{{ descriptions.mobile_short }}:</td>
                      <td>{{ student.mobile }}</td>
                    </tr>
                    <tr>
                      <td>{{ descriptions.address }}:</td>
                      <td>{{ student.address }}</td>
                    </tr>
                    <tr v-if="student.address_other">
                      <td>{{ descriptions.address_other }}:</td>
                      <td>{{ student.address_other }}</td>
                    </tr>
                  </tbody>
                </table>
                <v-btn
                  class="ml-0"
                  outlined
                  tile
                  depressed
                  small
                  color="primary"
                  :href="`/pdf/bilan-complet/${student.id}`"
                  target="_blank"
                >
                  Pdf apprenant
                  <v-icon right color="primary">mdi-file-pdf-box</v-icon>
                </v-btn>
                <v-btn
                  class="ml-5"
                  outlined
                  tile
                  depressed
                  small
                  color="primary lighten-1"
                  :href="`/pdf/bilan-complet/${student.id}?see`"
                  target="_blank"
                >
                  Fiche apprenant
                  <v-icon right color="primary lighten-1">mdi-text-box</v-icon>
                </v-btn>
              </v-card-text>
            </v-card>
            <!-- formation info -->
            <v-card color="indigo lighten-5" tile flat>
              <v-card-title class="text-h6">
                <div>{{ descriptions.studies }}</div>
                <v-btn
                  v-if="!isEducationEditMode && !isStudentEditMode"
                  color="green"
                  dark
                  class="mx-5"
                  small
                  @click="createEducation"
                >
                  {{ descriptions.add }}
                </v-btn>
                <v-btn v-if="isEducationEditMode" color="orange" dark @click="cancelCreateEducation">
                  {{ descriptions.close_edit_mode }}
                  <v-icon right dark>mdi-close</v-icon>
                </v-btn>
              </v-card-title>
              <v-card-text v-if="!isEducationEditMode">
                <education-list
                  :student_id="student_id"
                  :hideActions="isStudentEditMode"
                  @editEducation="editEducation"
                ></education-list>
              </v-card-text>
            </v-card>
            <!-- assistance info -->
            <v-expansion-panels tile v-if="isAdmin && student">
              <v-expansion-panel>
                <v-expansion-panel-header class="text-subtitle-1">
                  {{ descriptions.as_id }}
                  <template v-if="student.assistant">
                    - {{ assistantDisplayName }}
                  </template>
                </v-expansion-panel-header>
                <v-expansion-panel-content>
                  <table style="width: 100%" class="table_details_referent">
                    <tbody>
                      <tr>
                        <v-btn
                          small
                          color="primary"
                          text
                          @click="navigateToAssistantByName(student.assistant.name)"
                          class="mx-0 mt-2 w-100"
                        >
                          <v-icon small class="mr-1">mdi-open-in-new</v-icon>
                          Info {{ assistantDisplayName }}
                        </v-btn>
                      </tr>
                      <tr>
                        <td class="body-2">{{ descriptions.sent_by }}:</td>
                        <td>{{ student.assistant.society }}</td>
                      </tr>
                      <tr>
                        <td class="body-2">{{ descriptions.mandat }}:</td>
                        <td>{{ student.assistant.mandat }}</td>
                      </tr>
                      <tr>
                        <td class="body-2">{{ descriptions.assistant_social }}:</td>
                        <td>{{ student.assistant.name }}</td>
                      </tr>
                      <tr>
                        <td class="body-2">{{ descriptions.email }}:</td>
                        <td>{{ student.assistant.email }}</td>
                      </tr>
                      <tr>
                        <td class="body-2">{{ descriptions.mobile_short }}:</td>
                        <td>{{ student.assistant.mobile }}</td>
                      </tr>
                      <tr>
                        <td class="body-2">{{ descriptions.address }}:</td>
                        <td>{{ student.assistant.address }}</td>
                      </tr>
                    </tbody>
                  </table>
                </v-expansion-panel-content>
              </v-expansion-panel>
            </v-expansion-panels>
            <courses-list :student_id="student_id"></courses-list>
          </v-col>
        </v-row>
      </v-col>
      <v-col cols="12" md="8" v-if="isStudentEditMode">
        <student-form :student="student" formTitle="" @save="onSave"></student-form>
      </v-col>
      <v-col cols="12" md="8" v-if="isEducationEditMode">
        <education-section
          :educationToEdit="educationToEdit"
          :student-id="student_id"
          @cancel="cancelCreateEducation"
        />
      </v-col>
      <!-- tabs -->
      <v-col cols="12" md="8" v-if="!isStudentEditMode && !isEducationEditMode">
        <v-row>
          <v-col>
            <v-card-title class="text-h6">
              <v-icon left>mdi-school</v-icon>
              {{ selected_sc?.course.name }}
            </v-card-title>
          </v-col>
        </v-row>

        <!-- Button Group for Tab Navigation -->
        <v-btn-toggle
          v-model="tab"
          @change="navigateToTab"
          class="ml-1"
          mandatory
          color="white"
          active-class="purple dark"
          borderless
        >
          <v-btn value="home" class="px-6 my-0">
            <v-icon left :class="{ 'white--text': tab === 'home' }">mdi-home</v-icon>
            Actualité
          </v-btn>
          <v-btn value="evaluations" class="px-6 my-0">
            <v-icon left :class="{ 'white--text': tab === 'evaluations' }">mdi-chart-bar</v-icon>
            Evaluations
          </v-btn>
          <v-btn value="attestations" class="px-12 my-0">
            <v-icon left :class="{ 'white--text': tab === 'attestations' }">mdi-certificate-outline</v-icon>
            Bilans
          </v-btn>
          <v-btn value="notes" class="px-12 my-0">
            <v-icon left :class="{ 'white--text': tab === 'notes' }">mdi-text-box-multiple</v-icon>
            Journal
          </v-btn>
        </v-btn-toggle>
        <!-- Content for each "tab" -->
        <v-container>
          <div v-if="isActive('home')">
            <v-col v-if="loading" class="text-center loader-container">
              <v-progress-circular indeterminate color="primary"></v-progress-circular>
            </v-col>
            <student-course-info :student_course_id="selected_sc_id" v-if="selected_sc_id" class="mb-4" />
            <student-presence-list :student_course_id="selected_sc_id" v-if="selected_sc_id"></student-presence-list>
            <student-checkin-list :student_course_id="selected_sc_id" v-if="selected_sc_id" class="mt-4"></student-checkin-list>
          </div>

          <div v-if="isActive('evaluations')">
            <v-row dense>
              <student-evaluation-list
                :student_id="student.id"
                :student_course="selected_sc"
                v-if="student && selected_sc_id"
                class="pt-5"
              />
            </v-row>
          </div>

          <div v-if="isActive('attestations')">
            <v-row dense>
              <attestation-list :student_course_id="selected_sc_id" v-if="selected_sc_id" class="pt-5" />
            </v-row>
          </div>

          <div v-if="isActive('notes')">
            <note-list :student_course_id="selected_sc_id" v-if="selected_sc_id" />
          </div>
        </v-container>
      </v-col>
    </v-row>
  </div>
</template>
<script>
import StudentCourse from "@/models/StudentCourse"
import Description from "@/models/Description"
import CoursesList from "@/components/students/StudentCoursesList"
import StudentCourseInfo from "@/components/students/StudentCourseInfo"
import StudentEvaluationList from "@/components/evaluations/StudentEvaluationList"
import StudentPresenceList from "@/components/students/StudentPresenceList"
import StudentCheckinList from "@/components/checkins/StudentCheckinList"
import NoteList from "@/components/notes/NoteList"
import EducationList from "@/components/educations/EducationList"
import EducationSection from "@/components/educations/EducationSection"
import StudentForm from "@/components/students/StudentForm.vue"
import Student from "@/models/Student"
import AttestationList from "@/components/attestations/AttestationList"
import { mapGetters } from "vuex"
import axios from "axios"

export default {
  props: {
    student_id: {
      required: true,
      type: Number,
    },
    create_mode: {
      required: false,
      type: Boolean,
      default: false,
    },
  },
  async mounted() {
    this.loading = true
    const queryTab = this.$route.query.tab
    if (queryTab) {
      this.tab = queryTab
    }
    await this.getData(this.student_id)
    this.$on("activeCourseEvent", function (msg) {
      this.selected_sc_id = msg
    })
    axios
      .get("/api/v1/languages")
      .then((response) => {
        this.languages = response.data
      })
      .catch((error) => {
        console.log(error)
      })
      .finally(() => {
        this.loading = false
      })
    if (this.create_mode) {
      this.isEducationEditMode = true
    }
  },
  data() {
    return {
      tab: "home",
      fab: false,
      student: null,
      loading: false,
      selected_sc_id: null,
      formTitle: "Modifier",
      languages: [],
      isStudentEditMode: false,
      isEducationEditMode: false,
      educationToEdit: null,
    }
  },
  components: {
    CoursesList,
    AttestationList,
    StudentPresenceList,
    StudentCheckinList,
    EducationList,
    StudentForm,
    StudentEvaluationList,
    StudentCourseInfo,
    EducationSection,
    NoteList,
  },
  watch: {
    "$route.query.tab"(newTab) {
      if (newTab) {
        this.tab = newTab
      }
    },
  },
  methods: {
    isActive(name) {
      return this.tab === name
    },
    navigateToTab() {
      this.$router.push({
        query: { ...this.$route.query, tab: this.tab },
      })
    },
    async getData(id) {
      await Student.getOne(id)
      this.student = Student.query().withAll().find(id)
    },
    async deleteStudent(student) {
      confirm(
        "Attention vous êtes en train de supprimer un élève, toutes les données seront perdues (présences, tests, cours suivis, details), êtes-vous sûr de votre action?"
      ) &&
        Student.deleteOne(student.id) &&
        this.$router.push("/students")
    },
    async finishDeletion() {
      await Student.deleteAll()
      this.$router.push("/students")
    },
    editStudent() {
      this.isStudentEditMode = true
    },
    async onSave(editedStudent) {
      this.loading = true
      Student.put(editedStudent)
        .then(async () => {
          await this.getData(editedStudent.id)
        })
        .finally(() => {
          this.loading = false
        })
    },
    createEducation() {
      this.educationToEdit = null
      this.isEducationEditMode = true
    },
    cancelCreateEducation() {
      this.isEducationEditMode = false
    },
    editEducation(education) {
      this.educationToEdit = education
      this.isEducationEditMode = true
    },
    closeEditionMode() {
      this.isStudentEditMode = false
      this.fab = false
    },
    navigateToAssistantByName(name) {
      this.$router.push({
        path: "/assistants",
        query: { search: name },
      })
    },
  },
  computed: {
    ...mapGetters("Auth", ["isAdmin", "isProfessor"]),
    selected_sc() {
      return StudentCourse.query().where("id", this.selected_sc_id).with("course").first()
    },
    isLanguageCourse() {
      return !!this.selected_sc?.isLanguageCourse
    },
    spokenLanguages() {
      if (this.student.langs?.length) {
        const filteredValues = this.student.langs.map((lang) => this.languages[lang])
        return filteredValues.join(", ")
      } else {
        return ""
      }
    },
    descriptions() {
      return Description.every()
    },
    assistantDisplayName() {
      if (!this.student?.assistant) return ''
      const { name, mandat, society } = this.student.assistant
      return `${name} (${mandat || society})`
    },
  },
}
</script>
<style scoped>
.details {
  width: 100%;
}

.v-icon--right {
  margin-left: 5px;
}

.v-btn:not(.v-btn--fab) {
  min-width: 50px;
  font-size: 12px;
  margin: 6px 6px;
}
</style>
